import PropTypes from 'prop-types'
import { Navigate } from 'react-router-dom'
import LoadingScreen from '@/components/LoadingScreen'
import { useBranding } from '@/contexts/BrandingContext'
import { isSignupEnabled } from '@/features/allauth/pages/account/selfServeRules'

/**
 * Hides the public signup front door (register / register-success / resend-verification)
 * when `brand.self_serve_signup_enabled` is off — ship-dark-enable-deliberately for
 * self-serve org signup. Redirects to `/login` rather than rendering a dead page, mirroring
 * how AuthGuard/GuestGuard return a page in place of children rather than a blank route.
 *
 * Deliberately NOT applied to `invite/:token` (a different flow with its own token) or
 * `verify-email/:key` (someone mid-verification when the flag flips must still be able to
 * finish) — see App.jsx for where those stay unwrapped.
 *
 * Waits for the branding fetch to resolve before deciding: `self_serve_signup_enabled`
 * defaults to `false` while loading (see BrandingContext's PLATFORM_DEFAULT_BRAND), and
 * redirecting on that transient default would bounce a legitimate visitor to `/login`
 * before the real value has even loaded.
 */
const SignupEnabledGuard = ({ children }) => {
    const { brand, isLoading } = useBranding()

    if (isLoading) {
        return <LoadingScreen />
    }

    if (!isSignupEnabled(brand.self_serve_signup_enabled)) {
        return <Navigate to="/login" replace />
    }

    return <>{children}</>
}

SignupEnabledGuard.propTypes = {
    children: PropTypes.node
}

export default SignupEnabledGuard
